import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useOutletContext, useParams } from "react-router-dom";
import consoleApi, { errorMessage } from "./consoleApi";
import {
  CARD, QUIET_AFTER_DAYS, StatusBadge, TenantMark, brandColor, daysSince, formatDate, formatDateTime,
  riskStatusLabel, staffTone, staffVerb, timeAgo,
} from "./format";
import { ActivityIcon, ArrowLeftIcon, BuildingIcon, ExternalIcon, MailIcon, ShieldIcon, UsersIcon } from "./icons";

function Section({ title, children, aside }) {
  return (
    <section className={CARD}>
      <div className="flex items-center justify-between gap-3 px-5 pb-1 pt-4">
        <h2 className="text-sm font-bold text-slate-900">{title}</h2>
        {aside}
      </div>
      <div className="px-5 pb-5 pt-3">{children}</div>
    </section>
  );
}

function Kpi({ icon: IconComponent, label, value, hint }) {
  return (
    <div className={`${CARD} px-4 py-4`}>
      <div className="flex items-center gap-2 text-slate-500">
        <IconComponent className="h-4 w-4" />
        <p className="text-xs font-medium">{label}</p>
      </div>
      <p className="mt-2 text-2xl font-bold tabular-nums text-slate-900">{value}</p>
      {hint && <p className="mt-0.5 text-xs text-slate-500">{hint}</p>}
    </div>
  );
}

// What each action does, shown before staff confirm it
const ACTIONS = {
  approve: {
    label: "Approve",
    title: "Approve this tenant?",
    body: (t) => `${t.companyName}'s users will be able to sign in at ${t.domain} straight away.`,
    button: "Approve",
    tone: "positive",
    path: "approve",
  },
  reject: {
    label: "Reject",
    title: "Reject this signup?",
    body: (t) => `This deletes ${t.companyName}, its admin account and department. Their domain and email are freed so they can submit the form again. This can't be undone.`,
    button: "Reject and delete",
    tone: "danger",
    path: "reject",
    reason: true,
  },
  suspend: {
    label: "Suspend",
    title: "Suspend this tenant?",
    body: (t) => `Everyone at ${t.companyName} is locked out immediately. Their data is kept, and you can reactivate them at any time.`,
    button: "Suspend",
    tone: "danger",
    path: "suspend",
    reason: true,
  },
  reactivate: {
    label: "Reactivate",
    title: "Reactivate this tenant?",
    body: (t) => `${t.companyName}'s users will be able to sign in again.`,
    button: "Reactivate",
    tone: "positive",
    path: "reactivate",
  },
};

const ACTIONS_FOR_STATUS = {
  pending: ["approve", "reject"],
  active: ["suspend"],
  suspended: ["reactivate"],
};

const SOLID = {
  positive: "bg-emerald-600 text-white hover:bg-emerald-700 shadow-sm shadow-emerald-900/10",
  danger: "bg-red-600 text-white hover:bg-red-700 shadow-sm shadow-red-900/10",
};
const OUTLINE = {
  positive: "bg-emerald-600 text-white hover:bg-emerald-700 shadow-sm shadow-emerald-900/10",
  danger: "border border-red-200 bg-white text-red-700 hover:bg-red-50",
};

function ConfirmPanel({ actionKey, tenant, onCancel, onDone }) {
  const action = ACTIONS[actionKey];
  const [reason, setReason] = useState("");
  const [notify, setNotify] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const confirm = async () => {
    setBusy(true);
    setError(null);
    try {
      const body = {};
      if (action.reason && reason.trim()) body.reason = reason.trim();
      if (actionKey === "approve") body.notify = notify;
      const res = await consoleApi.post(`/tenants/${tenant.id}/${action.path}`, body);
      onDone(actionKey, res.data.message);
    } catch (err) {
      setError(errorMessage(err, `Couldn't ${action.label.toLowerCase()} this tenant. Please try again.`));
      setBusy(false);
    }
  };

  const danger = action.tone === "danger";
  return (
    <div
      className={`rounded-2xl border p-5 ${danger ? "border-red-200 bg-red-50/60" : "border-emerald-200 bg-emerald-50/60"}`}
      role="dialog"
      aria-labelledby="confirm-title"
    >
      <h2 id="confirm-title" className="text-base font-bold text-slate-900">{action.title}</h2>
      <p className="mt-1 max-w-2xl text-sm text-slate-700">{action.body(tenant)}</p>

      {actionKey === "approve" && (
        <label className="mt-4 flex items-start gap-2.5 text-sm text-slate-700">
          <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} className="mt-0.5 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500" />
          <span>
            Email the admin{tenant.admins.length === 1 ? "" : "s"} that their site is ready
            {tenant.admins.length > 0 && <span className="text-slate-500"> ({tenant.admins.map((a) => a.email).join(", ")})</span>}
          </span>
        </label>
      )}

      {action.reason && (
        <div className="mt-4 max-w-2xl">
          <label htmlFor="action-reason" className="mb-1 block text-sm font-medium text-slate-700">
            Reason <span className="font-normal text-slate-500">(optional, kept in the staff activity log)</span>
          </label>
          <textarea
            id="action-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            maxLength={500}
            rows={2}
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
          />
        </div>
      )}

      {error && <p className="mt-3 text-sm font-medium text-red-700" role="alert">{error}</p>}

      <div className="mt-5 flex flex-wrap gap-2">
        <button onClick={confirm} disabled={busy} className={`rounded-lg px-4 py-2 text-sm font-semibold disabled:opacity-50 ${SOLID[action.tone]}`}>
          {busy ? "Working…" : action.button}
        </button>
        <button onClick={onCancel} disabled={busy} className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
          Cancel
        </button>
      </div>
    </div>
  );
}

export default function TenantDetail() {
  const { tenantId } = useParams();
  const navigate = useNavigate();
  const { refreshOverview } = useOutletContext();
  const [tenant, setTenant] = useState(null);
  const [error, setError] = useState(null);
  const [pending, setPending] = useState(null); // action key awaiting confirmation
  const [notice, setNotice] = useState(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await consoleApi.get(`/tenants/${tenantId}`);
      setTenant(res.data.data);
    } catch (err) {
      setError(err?.response?.status === 404 ? "This tenant doesn't exist. It may have been rejected." : errorMessage(err, "Couldn't load this tenant."));
    }
  }, [tenantId]);

  useEffect(() => { load(); }, [load]);

  const onDone = (actionKey, message) => {
    setPending(null);
    refreshOverview();
    if (actionKey === "reject") {
      navigate("/?status=pending", { replace: true });
      return;
    }
    setNotice(message);
    load();
  };

  const back = (
    <Link to="/" className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-900">
      <ArrowLeftIcon /> All tenants
    </Link>
  );

  if (error) {
    return (
      <div className="space-y-4">
        {back}
        <p className="text-sm text-red-600">{error}</p>
      </div>
    );
  }
  if (!tenant) {
    return (
      <div className="space-y-4">
        {back}
        <div className="h-48 animate-pulse rounded-2xl bg-slate-200/60" />
      </div>
    );
  }

  const color = brandColor(tenant);
  const branding = tenant.branding || {};
  const colors = [["Primary", branding.primaryColor], ["Secondary", branding.secondaryColor]].filter(([, c]) => c);
  const stages = Object.entries(tenant.risksByStatus || {}).sort((a, b) => b[1] - a[1]);
  const maxStage = Math.max(1, ...stages.map(([, n]) => n));
  const quietDays = daysSince(tenant.lastActivityAt);
  const quiet = tenant.status === "active" && (quietDays === null || quietDays >= QUIET_AFTER_DAYS);
  const domains = tenant.domains?.length ? tenant.domains : [tenant.domain];

  return (
    <div className="space-y-6">
      {back}

      {/* Header in the tenant's own brand colour */}
      <div className={`${CARD} overflow-hidden`}>
        <div
          className="h-24"
          style={{ background: `linear-gradient(120deg, ${color} 0%, ${color}cc 45%, ${color}55 100%)` }}
        />
        <div className="flex flex-wrap items-end justify-between gap-4 px-6 pb-5 pt-3">
          <div className="flex flex-wrap items-end gap-4">
            <div className="-mt-8 rounded-2xl bg-white p-1 shadow-md">
              <TenantMark tenant={tenant} size="lg" />
            </div>
            <div className="pb-0.5">
              <div className="flex flex-wrap items-center gap-2.5">
                <h1 className="text-xl font-bold tracking-tight text-slate-900">{tenant.companyName}</h1>
                <StatusBadge status={tenant.status} />
              </div>
              <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-slate-600">
                {domains.map((d) =>
                  tenant.status === "active" ? (
                    <a key={d} href={`https://${d}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-indigo-700 hover:underline">
                      {d} <ExternalIcon className="h-3.5 w-3.5" />
                    </a>
                  ) : (
                    <span key={d}>{d}</span>
                  )
                )}
              </p>
            </div>
          </div>
          {!pending && (
            <div className="flex gap-2">
              {(ACTIONS_FOR_STATUS[tenant.status] || []).map((key) => (
                <button
                  key={key}
                  onClick={() => { setNotice(null); setPending(key); }}
                  className={`rounded-lg px-4 py-2 text-sm font-semibold ${OUTLINE[ACTIONS[key].tone]}`}
                >
                  {ACTIONS[key].label}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="flex flex-wrap gap-x-6 gap-y-1 border-t border-slate-100 px-6 py-3 text-xs text-slate-500">
          <span>Signed up {formatDateTime(tenant.createdAt)}</span>
          {tenant.approvedAt && <span>Approved {formatDate(tenant.approvedAt)}</span>}
          <span className={quiet ? "font-medium text-amber-700" : ""}>
            Last activity: {timeAgo(tenant.lastActivityAt)}
            {quiet && tenant.lastActivityAt && ` · quiet for ${quietDays} days`}
          </span>
        </div>
      </div>

      {pending && <ConfirmPanel actionKey={pending} tenant={tenant} onCancel={() => setPending(null)} onDone={onDone} />}
      {notice && (
        <p className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-800" role="status">{notice}</p>
      )}

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Kpi icon={UsersIcon} label="Active users" value={tenant.counts.users} hint={tenant.counts.deactivatedUsers ? `${tenant.counts.deactivatedUsers} deactivated` : null} />
        <Kpi icon={BuildingIcon} label="Risks" value={tenant.counts.risks} hint={`${tenant.counts.risksLast30Days} added in the last 30 days`} />
        <Kpi icon={ShieldIcon} label="Departments" value={tenant.departments.length} />
        <Kpi icon={ActivityIcon} label="Perspectives / objectives" value={`${tenant.counts.perspectives} / ${tenant.counts.objectives}`} />
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <Section title="Risks by stage" aside={<span className="text-xs text-slate-500">{tenant.counts.risks} total</span>}>
            {stages.length === 0 ? (
              <p className="text-sm text-slate-500">
                {tenant.status === "pending" ? "They can start adding risks once approved." : "No risks yet."}
              </p>
            ) : (
              <ul className="space-y-3">
                {stages.map(([status, n]) => (
                  <li key={status} className="grid grid-cols-[minmax(0,11rem)_1fr_2.5rem] items-center gap-3 text-sm">
                    <span className="truncate text-slate-700">{riskStatusLabel(status)}</span>
                    <span className="h-2.5 overflow-hidden rounded-full bg-slate-100">
                      <span className="block h-full rounded-full" style={{ width: `${(n / maxStage) * 100}%`, background: color }} />
                    </span>
                    <span className="text-right font-semibold tabular-nums text-slate-900">{n}</span>
                  </li>
                ))}
              </ul>
            )}
          </Section>

          <Section title="Staff actions">
            {tenant.staffActions.length === 0 ? (
              <p className="text-sm text-slate-500">No staff actions yet.</p>
            ) : (
              <ol className="relative space-y-4 border-l border-slate-200 pl-5">
                {tenant.staffActions.map((a) => (
                  <li key={a.id} className="relative text-sm">
                    <span className={`absolute -left-[25px] top-1.5 h-2.5 w-2.5 rounded-full ring-4 ring-white ${staffTone(a.action)}`} />
                    <p className="text-slate-700">
                      <span className="font-semibold text-slate-900">{a.staffEmail}</span> {staffVerb(a.action)}
                    </p>
                    <p className="text-xs text-slate-500">{formatDateTime(a.createdAt)}</p>
                    {a.details?.reason && <p className="mt-1 rounded-lg bg-slate-50 px-3 py-1.5 text-xs text-slate-600">“{a.details.reason}”</p>}
                  </li>
                ))}
              </ol>
            )}
          </Section>
        </div>

        <div className="space-y-6">
          <Section title={`Admin${tenant.admins.length === 1 ? "" : "s"}`}>
            {tenant.admins.length === 0 ? (
              <p className="text-sm text-slate-500">No admins.</p>
            ) : (
              <ul className="space-y-3">
                {tenant.admins.map((a) => (
                  <li key={a.id} className="flex items-center gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-bold text-white" style={{ background: color }}>
                      {(a.firstName?.[0] || "") + (a.lastName?.[0] || "")}
                    </span>
                    <div className="min-w-0 flex-1 text-sm">
                      <p className="truncate font-medium text-slate-900">
                        {a.firstName} {a.lastName}
                        {a.isDeleted && <span className="ml-1.5 text-xs font-normal text-slate-500">(deactivated)</span>}
                      </p>
                      <p className="truncate text-xs text-slate-500">{a.email}</p>
                    </div>
                    <a href={`mailto:${a.email}`} title={`Email ${a.email}`} aria-label={`Email ${a.email}`} className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700">
                      <MailIcon />
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </Section>

          <Section title="Departments" aside={<span className="text-xs text-slate-500">{tenant.departments.length}</span>}>
            {tenant.departments.length === 0 ? (
              <p className="text-sm text-slate-500">None.</p>
            ) : (
              <ul className="flex flex-wrap gap-1.5">
                {tenant.departments.map((d) => (
                  <li key={d.id} className="rounded-md bg-slate-100 px-2 py-1 text-xs font-medium text-slate-700">{d.name}</li>
                ))}
              </ul>
            )}
          </Section>

          <Section title="Branding">
            {colors.length === 0 && !branding.logoUrl ? (
              <p className="text-sm text-slate-500">Default branding.</p>
            ) : (
              <div className="space-y-3">
                {colors.length > 0 && (
                  <div className="flex gap-3">
                    {colors.map(([label, c]) => (
                      <div key={label} className="flex-1">
                        <div className="h-12 rounded-lg ring-1 ring-inset ring-black/5" style={{ background: c }} />
                        <p className="mt-1.5 text-xs font-medium text-slate-700">{label}</p>
                        <p className="font-mono text-[11px] uppercase text-slate-500">{c}</p>
                      </div>
                    ))}
                  </div>
                )}
                {branding.logoUrl && <p className="break-all text-xs text-slate-500">Logo: {branding.logoUrl}</p>}
              </div>
            )}
          </Section>

          <p className="px-1 text-xs text-slate-400">
            Tenant ID <span className="select-all font-mono">{tenant.id}</span>
          </p>
        </div>
      </div>
    </div>
  );
}
